import type { DeepActivityEvent } from "@/stores/ai-explorer-store"
import type { TFunction } from "i18next"

import {
  extractFilePath,
  extractTaskBrief,
  readTodosFromEvent,
  summarizeTodos,
} from "./activity-row-helpers"

type DetailPair = {
  tool: string
  source: string
  start: DeepActivityEvent | null
  end?: DeepActivityEvent | null
}

const FILE_PATH_KEYS = [
  "path",
  "file_path",
  "filepath",
  "filename",
  "file",
  "file_name",
  "query",
] as const

function recordValue(value: unknown): Record<string, unknown> | null {
  return value && typeof value === "object" && !Array.isArray(value)
    ? (value as Record<string, unknown>)
    : null
}

function stringValue(
  source: Record<string, unknown> | null | undefined,
  keys: readonly string[]
): string | null {
  if (!source) return null
  for (const key of keys) {
    const value = source[key]
    if (typeof value === "string" && value.trim()) return value.trim()
    if (typeof value === "number" && Number.isFinite(value)) return String(value)
  }
  return null
}

function numberValue(
  source: Record<string, unknown> | null | undefined,
  keys: readonly string[]
): number | null {
  if (!source) return null
  for (const key of keys) {
    const value = source[key]
    if (typeof value === "number" && Number.isFinite(value)) return value
    if (typeof value === "string" && value.trim() && Number.isFinite(Number(value))) {
      return Number(value)
    }
  }
  return null
}

function firstPreviewItem(
  summary: Record<string, unknown> | null | undefined
): Record<string, unknown> | null {
  const raw = summary?.preview_items
  if (!Array.isArray(raw)) return null
  return recordValue(raw[0])
}

function joinDetailParts(parts: Array<string | null | undefined>): string {
  return parts
    .filter((part): part is string => typeof part === "string" && part.length > 0)
    .join(" · ")
}

function detailLabel(t: TFunction, key: string, fallback: string): string {
  return t(`deepExplorer.detail_labels.${key}`, { defaultValue: fallback })
}

function fileLine(
  paired: DetailPair,
  endSummary: Record<string, unknown> | undefined
): string | null {
  if (paired.tool !== "read_file" && paired.tool !== "write_file" && paired.tool !== "edit_file") {
    return null
  }
  return extractFilePath(paired.start) ?? stringValue(endSummary, FILE_PATH_KEYS)
}

function documentLine({
  paired,
  endSummary,
  filePath,
  t,
}: {
  paired: DetailPair
  endSummary: Record<string, unknown> | undefined
  filePath: string | null
  t: TFunction
}): string | null {
  if (filePath) return null
  const startSummary = paired.start?.input_summary
  const docRef = recordValue(endSummary?.document_reference)
  const preview = firstPreviewItem(endSummary)
  const title =
    stringValue(endSummary, ["title", "name", "sourcefile", "sourcepage"]) ??
    stringValue(startSummary, ["title", "name", "path"]) ??
    stringValue(docRef, ["title", "sourcefile", "sourcepage"]) ??
    stringValue(preview, ["title", "sourcefile", "sourcepage"])

  if (!title) return null

  const documentId =
    stringValue(endSummary, ["document_id", "id"]) ??
    stringValue(startSummary, ["document_id", "id"]) ??
    stringValue(docRef, ["document_id", "id"]) ??
    stringValue(preview, ["document_id", "id"])
  const library = stringValue(endSummary, ["library"]) ?? stringValue(preview, ["library"])
  const page =
    stringValue(endSummary, ["sourcepage", "page"]) ??
    stringValue(docRef, ["sourcepage", "page"]) ??
    stringValue(preview, ["sourcepage", "page"])
  const version =
    numberValue(endSummary, ["version"]) ??
    numberValue(docRef, ["version"]) ??
    numberValue(preview, ["version"])

  return `${detailLabel(t, "document", "Document")}: ${joinDetailParts([
    title,
    documentId ? `${detailLabel(t, "id", "id")}: ${documentId}` : null,
    version != null ? `v${version}` : null,
    library,
    page,
  ])}`
}

function truncateLine(text: string, max: number): string {
  const normalized = text.replace(/\s+/g, " ").trim()
  if (normalized.length <= max) return normalized
  return `${normalized.slice(0, max - 1).trimEnd()}…`
}

function errorLine(summary: Record<string, unknown> | undefined, t: TFunction): string | null {
  if (!summary || summary.success !== false) return null
  const msg = stringValue(summary, ["error"])
  if (!msg) return null
  return `${detailLabel(t, "failed", "Failed")}: ${truncateLine(msg, 240)}`
}

/**
 * Pull the orchestrator's full task brief off a `task` tool_start event.
 * The brief carries the orchestrator's intent for the dispatched subagent
 * — one of the most informative pieces of the trace, but currently truncated
 * to a single 200-char detail line. Return both a single-line `preview` and
 * the unmodified `full` text so WorkerLanes can render a click-to-expand
 * block under the tool row.
 */
export function extractTaskBriefBlock(
  paired: DetailPair
): { preview: string; full: string } | null {
  if (paired.tool !== "task") return null
  const full = extractTaskBrief(paired.start)
  if (!full) return null
  return {
    preview: truncateLine(full, 160),
    full,
  }
}

/**
 * Lenient boolean coerce for the qa_reviewer preview. Returns "true" /
 * "false" only when the value is unambiguous (real boolean or
 * canonical string form); "?" otherwise so the preview doesn't silently
 * misreport a missing or unparseable field.
 */
function formatPassesPreview(value: unknown): string {
  if (value === true) return "true"
  if (value === false) return "false"
  if (typeof value === "string") {
    const lowered = value.trim().toLowerCase()
    if (lowered === "true") return "true"
    if (lowered === "false") return "false"
  }
  return "?"
}

/**
 * Lenient numeric coerce for the qa_reviewer preview. Accepts a number
 * or a numeric string (the LLM occasionally emits ``"score": "82"``).
 * Returns "?" for missing / non-numeric / NaN / Infinity so a bad value
 * doesn't render as "score=NaN".
 */
function formatScorePreview(value: unknown): string {
  if (typeof value === "number" && Number.isFinite(value)) return `${value}`
  if (typeof value === "string" && value.trim() !== "") {
    const n = Number(value)
    if (Number.isFinite(n)) return `${n}`
  }
  return "?"
}

/**
 * Pull the qa_reviewer's verdict (the JSON body of /quality_review.json)
 * off a write_file or read_file tool row that touched that path. The
 * streaming layer enriches the tool_end ``result_summary`` with the
 * captured file content (capped at ~8 KB upstream) so the UI can render
 * an inline "Show verdict" expander — same pattern as the task brief
 * (extractTaskBriefBlock) and the subagent report (extractSubagentReport).
 *
 * Preview tries to extract a one-line headline from the JSON (passes,
 * score, issue count) so the collapsed row is informative even without
 * expanding. Falls back to the raw truncated text on parse failure.
 */
export function extractQualityReviewBlock(
  paired: DetailPair
): { preview: string; full: string } | null {
  if (paired.tool !== "write_file" && paired.tool !== "read_file") return null
  const content = stringValue(paired.end?.result_summary, ["quality_review_content"])
  if (!content) return null
  let preview = truncateLine(content, 160)
  // Best-effort: pull verdict + score + issue count for the collapsed
  // preview line. The streaming layer stores the raw JSON the model
  // wrote, so a leading/trailing code fence is possible.
  try {
    const stripped = content
      .trim()
      .replace(/^```[a-z]*\n?/i, "")
      .replace(/\n?```\s*$/, "")
    const parsed: unknown = JSON.parse(stripped)
    if (parsed && typeof parsed === "object") {
      const obj = parsed as Record<string, unknown>
      // qa_reviewer's structured-output schema asks for boolean ``passes``
      // and numeric ``score``, but in practice the LLM occasionally
      // stringifies them ("true" / "82"). The strict ``=== true`` check
      // used to misreport every string verdict as "passes=false", and
      // ``typeof === "number"`` would drop a stringified score to "?".
      // Coerce both leniently here so the preview reflects what the
      // model actually said. Bot review on PR #1964.
      const passesText = formatPassesPreview(obj.passes)
      const scoreText = formatScorePreview(obj.score)
      const issues = Array.isArray(obj.issues) ? obj.issues.length : 0
      preview = `passes=${passesText} · score=${scoreText} · ${issues} issue${issues === 1 ? "" : "s"}`
    }
  } catch {
    // Keep the truncated raw text from above.
  }
  return { preview, full: content }
}

/**
 * Pull the subagent's report text off a `task` row. Prefers the LIVE token
 * stream (B7 — the dispatch's accumulating chunks) while the task is still
 * running; falls back to `result_summary.output_text` after tool_end. The
 * `live` flag lets WorkerLanes pick a "Live report" label with streaming
 * affordances vs. the final "Show report".
 *
 * The wire field is `result_summary.output_text`, capped at
 * `_SUBAGENT_OUTPUT_CAP` by streaming.py for the buffered final form. Live
 * deltas come on `deep_subagent_chunk` frames.
 */
export function extractSubagentReport(
  paired: DetailPair,
  liveSubagentReports?: Record<string, string>
): { preview: string; full: string; live: boolean } | null {
  if (paired.tool !== "task") return null
  // Prefer live text while the task is running — the dispatch is keyed on
  // the subagent's `dispatch_id`, available on the tool_start event.
  const dispatchId = paired.start?.dispatch_id ?? paired.end?.dispatch_id ?? null
  if (!paired.end && dispatchId && liveSubagentReports?.[dispatchId]) {
    const live = liveSubagentReports[dispatchId]
    return { preview: truncateLine(live, 160), full: live, live: true }
  }
  const summary = paired.end?.result_summary
  if (!summary) return null
  const text = stringValue(summary, ["output_text"])
  if (!text) return null
  return { preview: truncateLine(text, 160), full: text, live: false }
}

function topSnippetLine(summary: Record<string, unknown> | undefined, t: TFunction): string | null {
  const items = summary?.preview_items
  if (!Array.isArray(items) || items.length !== 1) return null
  // Only render the inline "Top result" line for single-result searches.
  // When the tool returned multiple items, the click-to-expand list (B1)
  // is the canonical UI — surfacing one snippet inline as well would
  // duplicate the first list entry.
  const first = recordValue(items[0])
  const snippet = stringValue(first, ["snippet"])
  if (!snippet) return null
  return `${detailLabel(t, "top_result", "Top result")}: ${truncateLine(snippet, 200)}`
}

export interface SearchPreviewItem {
  title: string
  snippet: string
  sourceId: string | null
}

/**
 * Pull the full preview list off a search-style tool's tool_end. Backend caps
 * at 5 entries (see `_extract_result_summary` in streaming.py). Returns null
 * for non-search tools or when fewer than two entries are present — the
 * single-item case is handled by the inline `Top result` detail line so the
 * UI doesn't show both. WorkerLanes renders this as a click-to-expand block
 * under the per-tool detail rows.
 */
export function extractSearchPreviews(paired: DetailPair): SearchPreviewItem[] | null {
  const items = paired.end?.result_summary?.preview_items
  if (!Array.isArray(items) || items.length < 2) return null
  const out: SearchPreviewItem[] = []
  for (const item of items) {
    const rec = recordValue(item)
    const snippet = stringValue(rec, ["snippet"])
    if (!snippet) continue
    out.push({
      title: stringValue(rec, ["title", "name", "url", "sourcefile", "sourcepage"]) ?? "",
      snippet,
      sourceId: stringValue(rec, ["library", "url", "id", "document_id"]),
    })
  }
  return out.length > 0 ? out : null
}

function resultLine(summary: Record<string, unknown> | undefined, t: TFunction): string | null {
  const resultCount =
    numberValue(summary, ["result_count"]) ??
    (Array.isArray(summary?.preview_items) ? summary.preview_items.length : null)
  const truncated = summary?.truncated === true

  if (resultCount == null && !truncated) return null

  const resultText =
    resultCount != null
      ? `${resultCount} ${t("deepExplorer.result", {
          count: resultCount,
          defaultValue: resultCount === 1 ? "result" : "results",
        })}`
      : null

  return `${detailLabel(t, "result", "Result")}: ${joinDetailParts([
    resultText,
    truncated ? detailLabel(t, "truncated", "truncated") : null,
  ])}`
}

/**
 * Build reader-friendly details for the Show details mode in worker lanes.
 * The headline remains humanized; these lines expose raw tool/source plus
 * document/query/result context from both tool_start input and tool_end output.
 */
export function buildToolDetailLines(paired: DetailPair, t: TFunction): string[] {
  const startSummary = paired.start?.input_summary
  const endSummary = paired.end?.result_summary
  // Headline already carries the humanized tool phrase; the lane pill carries
  // the source role. Repeating raw `tool · source` here was the noisiest line
  // in every tool row (Erik 2026-05-04).
  const lines: string[] = []

  // Brief is rendered separately by WorkerLanes via `extractTaskBriefBlock`
  // (B3) as a click-to-expand block, so we deliberately don't append it
  // to the detail strings list — that would duplicate it.

  const filePath = fileLine(paired, endSummary)
  if (filePath) {
    lines.push(`${detailLabel(t, "file", "File")}: ${filePath}`)
  }

  const document = documentLine({ paired, endSummary, filePath, t })
  if (document) lines.push(document)

  const query =
    stringValue(startSummary, ["query", "q", "search_query"]) ?? stringValue(endSummary, ["query"])
  if (query) {
    lines.push(`${detailLabel(t, "query", "Query")}: ${query}`)
  }

  const todos = paired.tool === "write_todos" ? readTodosFromEvent(paired.start) : null
  if (todos) {
    const counts = summarizeTodos(todos)
    lines.push(
      `${detailLabel(t, "todos", "Todos")}: ${counts.done} ${detailLabel(t, "done", "done")} · ${counts.in_progress} ${detailLabel(t, "running", "running")} · ${counts.pending} ${detailLabel(t, "pending", "pending")}`
    )
  }

  const result = resultLine(endSummary, t)
  if (result) lines.push(result)

  const topSnippet = topSnippetLine(endSummary, t)
  if (topSnippet) lines.push(topSnippet)

  const error = errorLine(endSummary, t)
  if (error) lines.push(error)

  // Subagent report (`task` tool output_text) is rendered separately by
  // WorkerLanes via `extractSubagentReport` so it can be click-to-expanded
  // — not joined into this strings list.

  return lines
}
